// Linking Google, Discord or Steam from a PC during a session (the station link handoff). The PC opens this page in its
// locked-down sign-in window as `/station-link?via=<provider>#<secret>`. The page:
//
//   1. takes the secret out of the URL at once and keeps it in memory only;
//   2. inside the PC's own window (its user agent carries the station's marker), asks the api for the provider's own page
//      (`POST /v1/auth/station-link/start`), which starts the link for the player sitting at that PC, and goes straight
//      there: the player never picks the provider again, and nobody signs in to this site. Anywhere else (a link sent to
//      somebody's phone or browser) it first asks "Link Google to ab***@g***.com at PC 07?" with Continue and Cancel
//      (`POST /v1/station-link/lookup`), so nobody links their own provider to another person's account unawares;
//   3. comes back here once, `?linked=1` or with the provider's `?error=`, and says so. The PC sees the result on its
//      own and closes the window.
//
// The provider's name survives the round trip in this tab's `sessionStorage` (never the secret). Every failure is said
// in words; no raw code reaches the screen.

import { useTranslation } from '@clavian/i18n/react';
import type { StationLinkView } from '@clavian/api-client';
import { Button, Spinner } from '@clavian/ui-web';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { AuthLayout } from '../auth/AuthLayout';
import { providerNavigation } from '../auth/social';
import { tokenFromFragment } from '../station-sign-in/handoff-token';

export const STATION_LINK_PROVIDERS = ['google', 'discord', 'steam'] as const;
export type StationLinkProvider = (typeof STATION_LINK_PROVIDERS)[number];

export const isStationLinkProvider = (value: unknown): value is StationLinkProvider =>
  typeof value === 'string' && (STATION_LINK_PROVIDERS as readonly string[]).includes(value);

/** What `/station-link` reads from its query: the provider asked for, and what a round trip came back with. */
export interface StationLinkSearch {
  readonly via?: StationLinkProvider;
  readonly linked?: true;
  readonly error?: string;
}

export function validateStationLinkSearch(search: Record<string, unknown>): StationLinkSearch {
  const error = typeof search['error'] === 'string' && /^[A-Za-z0-9_.-]{1,64}$/.test(search['error']) ? search['error'] : undefined;
  return {
    ...(isStationLinkProvider(search['via']) ? { via: search['via'] } : {}),
    ...(search['linked'] === 1 || search['linked'] === '1' || search['linked'] === true ? { linked: true as const } : {}),
    ...(error === undefined ? {} : { error }),
  };
}

const PROVIDER_KEY = 'clavian.stationLink.provider';

function rememberProvider(provider: StationLinkProvider | undefined): void {
  try {
    if (provider === undefined) window.sessionStorage.removeItem(PROVIDER_KEY);
    else window.sessionStorage.setItem(PROVIDER_KEY, provider);
  } catch {
    // Storage refused: the page still works, it just names no provider on the way back.
  }
}

function rememberedProvider(): StationLinkProvider | undefined {
  try {
    const stored = window.sessionStorage.getItem(PROVIDER_KEY);
    return isStationLinkProvider(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export type StationLinkFailure = 'taken' | 'unverified' | 'cancelled' | 'expired' | 'missing' | 'network' | 'generic';

/** What a provider's `?error=` means here. */
export function linkFailureOf(error: string, provider: StationLinkProvider | undefined): StationLinkFailure {
  switch (error) {
    case 'account_already_linked_to_different_user':
      return 'taken';
    case 'unable_to_link_account':
      return provider === 'discord' ? 'unverified' : 'generic';
    case 'access_denied':
      return 'cancelled';
    case 'state_not_found':
    case 'state_mismatch':
      return 'expired';
    default:
      return 'generic';
  }
}

type View =
  | { readonly kind: 'opening' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'confirm'; readonly view: StationLinkView }
  | { readonly kind: 'linked' }
  | { readonly kind: 'cancelled' }
  // `message`: the words of the server's problem code, for a refusal that is none of the named ones.
  | { readonly kind: 'failed'; readonly failure: StationLinkFailure; readonly message?: string };

/**
 * The token the station's own sign-in window carries in its user agent (the agent's `--user-agent`, ADR-0053). Not a
 * security boundary: the player controls that window. It only spares the genuine player a confirm tap; a link sent to
 * someone else opens in a browser that never carries it, which gets the confirm screen.
 */
export const STATION_WINDOW_MARKER = 'ClavianStationWindow/1';

/** Whether this page runs inside the station's own sign-in window. */
export const inStationWindow = (userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean =>
  userAgent.includes(STATION_WINDOW_MARKER);

export function StationLinkPage(): ReactNode {
  const { t } = useTranslation('player');
  const problem = useProblem();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const search = useSearch({ strict: false }) as StationLinkSearch;

  // Read once, on arrival, and never stored.
  const [token] = useState(() => {
    const fragment = tokenFromFragment(location.hash);
    return typeof fragment === 'string' ? fragment : null;
  });
  const [provider] = useState<StationLinkProvider | undefined>(() => search.via ?? rememberedProvider());
  // In the PC's own window the link starts at once; anywhere else the page first says what it would link, to whom.
  const [view, setView] = useState<View>(() => {
    if (search.linked === true) return { kind: 'linked' };
    if (search.error !== undefined) return { kind: 'failed', failure: linkFailureOf(search.error, provider) };
    if (token === null) return { kind: 'failed', failure: 'missing' };
    return inStationWindow() ? { kind: 'opening' } : { kind: 'checking' };
  });

  const failedView = (error: unknown): View => {
    const code = problemCode(error);
    if (code === 'LINK_HANDOFF_UNAVAILABLE' || code === PROBLEM.signInHandoffUnavailable) return { kind: 'failed', failure: 'expired' };
    if (code === undefined) return { kind: 'failed', failure: 'network' };
    return { kind: 'failed', failure: 'generic', message: problem.text(error, { fallback: t('stationLink.failure.generic', { provider: provider === undefined ? t('stationLink.provider.google') : t(`stationLink.provider.${provider}`) }) }) };
  };

  const go = (): void => {
    if (token === null) return;
    api
      .stationLinkStart({ body: { token } })
      .then(({ url }) => {
        providerNavigation.assign(url);
      })
      .catch((error: unknown) => setView(failedView(error)));
  };

  const started = useRef(false);
  useEffect(() => {
    if (started.current || token === null || (view.kind !== 'opening' && view.kind !== 'checking')) return;
    started.current = true;
    rememberProvider(provider);
    // The secret leaves the address bar (and history) at once.
    void navigate({ to: '/station-link', search: provider === undefined ? {} : { via: provider }, replace: true, resetScroll: false });
    if (view.kind === 'opening') {
      go();
      return;
    }
    api
      .lookUpStationLink({ body: { token } })
      .then((answer) => setView({ kind: 'confirm', view: answer }))
      .catch((error: unknown) => setView(failedView(error)));
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back from the provider: nothing to keep.
  useEffect(() => {
    if (view.kind !== 'opening' && view.kind !== 'checking' && view.kind !== 'confirm') rememberProvider(undefined);
  }, [view.kind]);

  const name = provider === undefined ? undefined : t(`stationLink.provider.${provider}`);

  if (view.kind === 'opening' || view.kind === 'checking') {
    const title = view.kind === 'checking' ? t('stationLink.checking') : name === undefined ? t('stationLink.openingAny') : t('stationLink.opening', { provider: name });
    return (
      <AuthLayout title={title} homeLink={false}>
        <p className="pw-handoff__body" role="status">
          <Spinner size="sm" labelHidden />
        </p>
      </AuthLayout>
    );
  }

  if (view.kind === 'confirm') {
    const shown = t(`stationLink.provider.${view.view.provider}`);
    const values = { provider: shown, account: view.view.account, station: view.view.stationName, venue: view.view.venueName };
    return (
      <AuthLayout title={t('stationLink.confirm.title', values)} homeLink={false}>
        <div className="pw-form">
          <p className="pw-handoff__body">{t('stationLink.confirm.body', values)}</p>
          <p className="pw-handoff__body">{t('stationLink.confirm.onlyYours', values)}</p>
          <Button variant="primary" size="lg" block onClick={() => {
            setView({ kind: 'opening' });
            go();
          }}>
            {t('stationLink.confirm.continue')}
          </Button>
          <Button variant="secondary" size="lg" block onClick={() => setView({ kind: 'cancelled' })}>
            {t('stationLink.confirm.cancel')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (view.kind === 'linked') {
    return (
      <AuthLayout title={name === undefined ? t('stationLink.linkedTitleAny') : t('stationLink.linkedTitle', { provider: name })} homeLink={false}>
        <p className="pw-handoff__body" role="status">
          {t('stationLink.linked')}
        </p>
      </AuthLayout>
    );
  }

  if (view.kind === 'cancelled') {
    return (
      <AuthLayout title={t('stationLink.failure.cancelledTitle')} homeLink={false}>
        <p className="pw-handoff__body" role="status">
          {t('stationLink.failure.cancelled')}
        </p>
      </AuthLayout>
    );
  }

  const { failure } = view;
  const values = { provider: name ?? t('stationLink.provider.google') };
  const title = failure === 'network' ? t('stationLink.failure.genericTitle') : t(`stationLink.failure.${failure}Title`, values);
  return (
    <AuthLayout title={title} homeLink={false}>
      <p className="pw-handoff__body" role="alert">
        {view.message ?? t(`stationLink.failure.${failure}`, values)}
      </p>
    </AuthLayout>
  );
}
